import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Breadcrumbs,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import { useDropzone } from "react-dropzone";
import {
  useListStorages,
  useArtifactTree,
  useUploadObject,
  useDeleteObject,
  useSyncBucket,
  useSearchTreeObjects,
} from "../../hooks/useStorage";
import * as api from "../../api/storage/storage";
import Title from "../../components/Title";
import { pageHelp } from "../../components/help/pageHelp";
import Back from "../../components/Back";
import FormattedDateCell from "../../components/FormattedDateCell";
import { useSnackbar } from "../../context/SnackbarContext";
import { useLoader } from "../../context/LoaderContext";
import BucketSelector from "./components/BucketSelector";
import { formatSize } from "./components/utils/getUtils";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { JsonEditor } from "json-edit-react";

const fileIcon = (name: string) => {
  const ext = name.split(".").pop()?.toLowerCase();
  if (["json", "xml", "yaml", "yml"].includes(ext || ""))
    return <DataObjectIcon fontSize="small" sx={{ color: "#7c3aed" }} />;
  if (["png", "jpg", "jpeg", "gif", "svg", "webp"].includes(ext || ""))
    return <ImageOutlinedIcon fontSize="small" sx={{ color: "#0891b2" }} />;
  if (["zip", "tar", "gz", "tgz"].includes(ext || ""))
    return <FolderZipOutlinedIcon fontSize="small" sx={{ color: "#b45309" }} />;
  if (["log", "txt", "html", "htm", "md", "out", "err", "robot"].includes(ext || ""))
    return <ArticleOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />;
  return <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: "#64748b" }} />;
};

const getBreadcrumbs = (path: string) =>
  path
    ? path
        .replace(/\/$/, "")
        .split("/")
        .map((part, idx, arr) => ({
          label: part,
          path: arr.slice(0, idx + 1).join("/") + "/",
        }))
    : [];

export default function Artifacts() {
  const { showSnackbar } = useSnackbar();
  const { showLoader, hideLoader } = useLoader();
  const [bucket, setBucket] = useLocalStorage("selectedBucket", "");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    node: undefined as any,
  });
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState(0);
  const [customPath, setCustomPath] = useState("");
  const [currentPath, setCurrentPath] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [selectedMeta, setSelectedMeta] = useState({});

  const {
    data: buckets = [],
    isError: storageError,
    isLoading: storageLoading,
  } = useListStorages();
  // Cloud object storage is optional. When no provider is configured the
  // /storage endpoint errors (or returns no buckets) -> block the page instead
  // of showing a half-broken browser, consistent with the create-suite wizard.
  const cloudUnavailable = !storageLoading && (storageError || buckets.length === 0);

  const isSearchActive = !!globalFilter.trim();

  const {
    data: treeData = [],
    isLoading: isLoadingTree,
    refetch: refetchTree,
    isFetching: isFetchingTree,
  } = useArtifactTree(bucket, currentPath);

  const {
    data: searchData,
    isLoading: isLoadingSearch,
    refetch: refetchSearch,
    isFetching: isFetchingSearch,
  } = useSearchTreeObjects(bucket, currentPath, globalFilter, {
    enabled: !!bucket && !!globalFilter.trim(),
    keepPreviousData: true,
  });

  const tableData = useMemo(() => {
    if (isSearchActive && searchData) return searchData.tree || [];
    if (!isSearchActive && treeData) return treeData;
    return [];
  }, [isSearchActive, treeData, searchData]);

  useEffect(() => {
    if (isSearchActive && searchData?.expanded) {
      const expObj: Record<string, boolean> = {};
      for (const path of searchData.expanded) expObj[path] = true;
      setExpanded(expObj);
    } else {
      setExpanded({});
    }
  }, [isSearchActive, searchData]);

  const isLoading = isSearchActive ? isLoadingSearch : isLoadingTree;
  const isFetching = isSearchActive ? isFetchingSearch : isFetchingTree;

  const uploadMut = useUploadObject(bucket);
  const deleteMut = useDeleteObject(bucket);
  const syncBucketMut = useSyncBucket();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: useCallback(
      (droppedFiles: File[]) => setFiles((prev) => [...prev, ...droppedFiles]),
      []
    ),
    multiple: true,
    disabled: uploadMut.isPending,
  });

  const handleSignedUrlDownload = async (bucket, path, filename) => {
    try {
      const url = await api.generateSignedUrl(
        bucket,
        path,
        "GET",
        600,
        undefined,
        "attachment"
      );
      window.open(url, "_blank");
      showSnackbar("Download started", "success");
    } catch {
      showSnackbar("Download failed", "error");
    }
  };

  const handleDelete = (item: any) =>
    setDeleteDialog({ open: true, node: item });
  const confirmDelete = () => {
    if (!deleteDialog.node) return;
    showLoader();
    deleteMut.mutate(
      { path: deleteDialog.node.path },
      {
        onSuccess: () => {
          hideLoader();
          showSnackbar(`'${deleteDialog.node?.name}' deleted`, "success");
          if (isSearchActive) refetchSearch();
          else refetchTree();
          setDeleteDialog({ open: false, node: undefined });
        },
        onError: () => {
          hideLoader();
          showSnackbar("Failed to delete", "error");
        },
      }
    );
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 360,
        Cell: ({ row }) => {
          const isFolder = row.original.type === "folder";
          const clickable = isFolder && !isSearchActive;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pl: row.depth * 2.5,
                minHeight: 38,
                borderRadius: 1,
                cursor: clickable ? "pointer" : "default",
                "&:hover": clickable ? { color: "primary.main" } : undefined,
              }}
              onClick={() => clickable && setCurrentPath(row.original.path)}
            >
              {isFolder ? (
                <FolderIcon sx={{ color: "#E9672D" }} fontSize="small" />
              ) : (
                fileIcon(row.original.name)
              )}
              <Typography
                sx={{ fontSize: "0.85rem", fontWeight: isFolder ? 700 : 500 }}
              >
                {row.original.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: "type",
        header: "Type",
        size: 110,
        Cell: ({ row }) => {
          const { type, content_type } = row.original;
          const label =
            type === "folder"
              ? "Folder"
              : (content_type || row.original.name?.split(".").pop() || "File")
                  .toString()
                  .toUpperCase();
          return (
            <Chip
              label={label}
              size="small"
              variant={type === "folder" ? "filled" : "outlined"}
              color={type === "folder" ? "primary" : "default"}
              sx={{ fontWeight: 600, maxWidth: 140 }}
            />
          );
        },
      },
      {
        accessorKey: "size",
        header: "Size",
        size: 90,
        Cell: ({ cell, row }) =>
          row.original.type === "folder" ? (
            <Typography sx={{ color: "text.disabled" }}>—</Typography>
          ) : (
            <Typography sx={{ fontSize: "0.82rem" }}>
              {formatSize(cell.getValue<number>())}
            </Typography>
          ),
      },
      {
        accessorKey: "updated_at",
        header: "Modified",
        size: 170,
        Cell: ({ row }) => {
          const v = row.original.updated_at || row.original.created_at;
          return v ? (
            <FormattedDateCell value={v} />
          ) : (
            <Typography sx={{ color: "text.disabled" }}>—</Typography>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 160,
        enableSorting: false,
        Cell: ({ row }) => {
          const isFile = row.original.type === "file";
          const fullPath = row.original.full_path || row.original.path;
          return (
            <Stack direction="row" spacing={0.25} alignItems="center">
              {isFile && (
                <>
                  <Tooltip arrow title="Download">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSignedUrlDownload(bucket, fullPath, row.original.name)
                      }
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip arrow title="Copy download URL">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        try {
                          const url = await api.generateSignedUrl(
                            bucket,
                            fullPath,
                            "GET",
                            600,
                            undefined,
                            "attachment"
                          );
                          await navigator.clipboard.writeText(url);
                          showSnackbar("Download URL copied", "success");
                        } catch {
                          showSnackbar("Failed to copy URL", "error");
                        }
                      }}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip arrow title={`Copy s3:// URI`}>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(`s3://${bucket}/${fullPath}`);
                    showSnackbar("URI copied", "success");
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow title="Metadata">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedMeta(row.original.meta_data ?? {});
                    setMetaModalOpen(true);
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(row.original)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [bucket, showSnackbar, isSearchActive]
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    getSubRows: isSearchActive
      ? (row) => row.children ?? []
      : (row) => row.children ?? [],
    getRowId: (row) => row.path,
    enableExpanding: true,
    paginateExpandedRows: false,
    enableGlobalFilter: true,
    manualFiltering: true,
    muiTableContainerProps: { sx: { maxHeight: "calc(78vh - 205px)" } },
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
      expanded: expanded,
    },
    state: {
      globalFilter,
      isLoading,
      showSkeletons: isLoading,
      expanded,
    },
    onGlobalFilterChange: (val) => {
      setGlobalFilter(typeof val === "string" ? val : "");
      setCurrentPath("");
    },
    onExpandedChange: setExpanded,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.depth % 2 === 0 ? "background.paper" : "grey.50",
        transition: "background 0.3s",
      },
    }),
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Button
          variant="contained"
          onClick={() => setUploadOpen(true)}
          disabled={!bucket}
        >
          Upload
        </Button>
        <Tooltip title="Sync bucket">
          <span>
            <IconButton
              onClick={async () => {
                if (!bucket) return;
                showLoader();
                try {
                  await syncBucketMut.mutateAsync(bucket);
                  if (isSearchActive) refetchSearch();
                  else refetchTree();
                  showSnackbar("Bucket synchronized", "success");
                } catch (e: any) {
                  showSnackbar(
                    "Synchronization error: " + (e?.message || ""),
                    "error"
                  );
                }
                hideLoader();
              }}
              disabled={!bucket || syncBucketMut.isPending}
            >
              <SyncIcon
                color={syncBucketMut.isPending ? "disabled" : "action"}
              />
            </IconButton>
          </span>
        </Tooltip>
        {/* <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={async () => {
                showLoader();
                try {
                  if (isSearchActive) await refetchSearch();
                  else await refetchTree();
                  showSnackbar("Data refreshed", "success");
                } catch (e: any) {
                  showSnackbar(
                    "Failed to refresh: " + (e?.message || ""),
                    "error"
                  );
                }
                hideLoader();
              }}
              disabled={isFetching}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip> */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ fontSize: 14, ml: 2 }}
        >
          <b>Selected Path: </b>
          <Button
            onClick={() => setCurrentPath("")}
            disabled={!currentPath}
            size="small"
            sx={{ textTransform: "none", px: 1, minWidth: "unset" }}
          >
            Root
          </Button>
          {getBreadcrumbs(currentPath).map((bc, idx, arr) => (
            <Button
              key={bc.path}
              size="small"
              color={idx === arr.length - 1 ? "primary" : "inherit"}
              onClick={() => setCurrentPath(bc.path)}
              sx={{ textTransform: "none", px: 1, minWidth: "unset" }}
            >
              {bc.label}
            </Button>
          ))}
        </Breadcrumbs>
      </Box>
    ),
    enableRowActions: false,
  });

  const handleUpload = async () => {
    setProgress(0);
    showLoader();
    let uploaded = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        await uploadMut.mutateAsync({
          file: files[i],
          prefix: currentPath + (customPath || ""),
          metadata: {},
        });
        uploaded++;
        setProgress(Math.round(((i + 1) / files.length) * 100));
        showSnackbar(`Uploaded: ${files[i].name}`, "success");
      } catch (e) {
        showSnackbar(`Upload failed: ${files[i].name}`, "error");
      }
    }
    hideLoader();
    setUploadOpen(false);
    setFiles([]);
    setProgress(0);
    if (isSearchActive) refetchSearch();
    else refetchTree();
    showSnackbar(
      `${uploaded}/${files.length} files uploaded`,
      uploaded === files.length ? "success" : "warning"
    );
  };

  useEffect(() => {
    if (buckets.length === 0) return;
    if (!bucket || !buckets.includes(bucket)) {
      setBucket(buckets[0]);
    }
  }, [buckets, bucket, setBucket]);
  useEffect(() => {
    if (!bucket) return;
    (async () => {
      showLoader();
      try {
        await syncBucketMut.mutateAsync(bucket);
        if (isSearchActive) await refetchSearch();
        else await refetchTree();
        showSnackbar("Bucket synchronized", "success");
      } catch (e: any) {
        showSnackbar("Synchronization error: " + (e?.message || ""), "error");
      }
      hideLoader();
    })();
  }, [bucket]);

  useEffect(() => {
    if (!globalFilter.trim() && isSearchActive) refetchTree();
  }, [globalFilter, isSearchActive]);

  return (
    <>
      <Title help={pageHelp.artifacts.help} helpItems={pageHelp.artifacts.items}>
        Artifacts
      </Title>
      {storageLoading ? (
        <Typography align="center" color="text.secondary" sx={{ py: 6 }}>
          Loading storage…
        </Typography>
      ) : cloudUnavailable ? (
        <Box
          sx={{
            mt: 2,
            p: 6,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "background.paper",
          }}
        >
          <CloudOffIcon sx={{ fontSize: 56, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6">Cloud storage is not connected</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: "auto" }}>
            Artifact browsing needs an object-storage provider (Google Cloud
            Storage, Amazon S3, or Azure Blob). Configure{" "}
            <code>STORAGE_PROVIDER</code> and its credentials, then reload.
          </Typography>
        </Box>
      ) : (
        <>
          <BucketSelector buckets={buckets} bucket={bucket} setBucket={setBucket} />
          {bucket ? (
            <MaterialReactTable table={table} />
          ) : (
            <Typography align="center" color="text.secondary">
              Please select a bucket to view artifacts.
            </Typography>
          )}
        </>
      )}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Upload to <b>{bucket}</b>
          {currentPath && (
            <span style={{ color: "#1976d2" }}>/{currentPath}</span>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Button
                size="small"
                onClick={() => setCurrentPath("")}
                disabled={!currentPath}
                sx={{ textTransform: "none", px: 1, minWidth: "unset" }}
              >
                Root
              </Button>
              {getBreadcrumbs(currentPath).map((bc, idx, arr) => (
                <Button
                  key={bc.path}
                  size="small"
                  color={idx === arr.length - 1 ? "primary" : "inherit"}
                  onClick={() => setCurrentPath(bc.path)}
                  sx={{ textTransform: "none", px: 1, minWidth: "unset" }}
                >
                  {bc.label}
                </Button>
              ))}
            </Breadcrumbs>
          </Box>

          <Box display="flex" gap={1} alignItems="center" mb={2}>
            <TextField
              label="Create new folder"
              size="small"
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
              placeholder="folder-name"
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                if (customPath.trim()) {
                  let newPath = currentPath;
                  if (newPath && !newPath.endsWith("/")) newPath += "/";
                  setCurrentPath(newPath + customPath.trim() + "/");
                  setCustomPath("");
                }
              }}
              disabled={!customPath.trim()}
            >
              Create Folder
            </Button>
          </Box>

          <Box sx={{ mb: 2, fontSize: 14, color: "text.secondary" }}>
            Upload path:{" "}
            <b>
              /{bucket}/{currentPath}
            </b>
          </Box>

          {progress > 0 && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography align="center" sx={{ mt: 1 }}>
                Uploading… {Math.round(progress)}%
              </Typography>
            </Box>
          )}

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "primary.main" : "grey.400",
              borderRadius: 1,
              p: 4,
              textAlign: "center",
              bgcolor: isDragActive ? "grey.100" : "background.paper",
              pointerEvents: uploadMut.isPending ? "none" : "auto",
              cursor: uploadMut.isPending ? "not-allowed" : "pointer",
              mt: 2,
            }}
          >
            <input {...getInputProps()} />
            <Typography>
              {isDragActive
                ? "Drop files here…"
                : "Drag & drop files here, or click to select"}
            </Typography>
          </Box>

          {files.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Files to upload:
              </Typography>
              <List dense>
                {files.map((file, i) => (
                  <ListItem key={i}>
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadOpen(false)}
            disabled={uploadMut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={files.length === 0 || uploadMut.isPending}
          >
            {uploadMut.isPending ? "Uploading…" : `Upload (${files.length})`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, node: undefined })}
        maxWidth="xs"
      >
        <DialogTitle>Delete Artifact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{deleteDialog.node?.name}</b>?
          </Typography>
          {deleteDialog.node && (
            <Typography variant="body2" color="text.secondary">
              Path: <code>{deleteDialog.node.path}</code>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, node: undefined })}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMut.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={metaModalOpen}
        onClose={() => setMetaModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Meta Data</DialogTitle>
        <DialogContent>
          <JsonEditor
            minWidth={"100%"}
            data={selectedMeta}
            restrictAdd={true}
            restrictDelete={true}
            restrictEdit={true}
          />
        </DialogContent>
      </Dialog>

      <Back />
    </>
  );
}
